import {
  FunctionComponent,
  useMemo,
  useEffect,
  useState,
  useRef,
  useCallback,
  ElementRef,
  Fragment,
} from "react";
import { renderToString } from "react-dom/server";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { EditorConfig, icons } from "@ckeditor/ckeditor5-core";
import { Essentials } from "@ckeditor/ckeditor5-essentials";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Subscript,
  Superscript,
} from "@ckeditor/ckeditor5-basic-styles";
import { Paragraph } from "@ckeditor/ckeditor5-paragraph";
import { Heading } from "@ckeditor/ckeditor5-heading";
import { Link } from "@ckeditor/ckeditor5-link";
import {
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
} from "@ckeditor/ckeditor5-table";
import {
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
} from "@ckeditor/ckeditor5-font";
import { Alignment } from "@ckeditor/ckeditor5-alignment";
import { Indent, IndentBlock } from "@ckeditor/ckeditor5-indent";
import { DocumentList } from "@ckeditor/ckeditor5-list";
import { Autoformat } from "@ckeditor/ckeditor5-autoformat";
import { RemoveFormat } from "@ckeditor/ckeditor5-remove-format";
import ImageUtils from "@ckeditor/ckeditor5-image/src/imageutils";
import {
  AutoImage,
  Image,
  ImageCaption,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
} from "@ckeditor/ckeditor5-image";
import {
  SpecialCharacters,
  SpecialCharactersEssentials,
} from "@ckeditor/ckeditor5-special-characters";
import { BlockQuote } from "@ckeditor/ckeditor5-block-quote";
import { Highlight } from "@ckeditor/ckeditor5-highlight";
import { PageBreak } from "@ckeditor/ckeditor5-page-break";
import { HorizontalLine } from "@ckeditor/ckeditor5-horizontal-line";
import { Pagination } from "@ckeditor/ckeditor5-pagination";
import { ExportPdf } from "@ckeditor/ckeditor5-export-pdf";
import { HtmlEmbed } from "@ckeditor/ckeditor5-html-embed";
import { Autosave } from "@ckeditor/ckeditor5-autosave";
import { CloudServices } from "@ckeditor/ckeditor5-cloud-services";
import { ImportWord } from "@ckeditor/ckeditor5-import-word";
import { FormatPainter } from "@ckeditor/ckeditor5-format-painter";
import { Mention } from "@ckeditor/ckeditor5-mention";
import { SlashCommand } from "@ckeditor/ckeditor5-slash-command";
import { Template } from "@ckeditor/ckeditor5-template";
import {
  DocumentOutline,
  TableOfContents,
} from "@ckeditor/ckeditor5-document-outline";

import { InsightsWidgetButton } from "./plugins/InsightsWidget.plugin";
import { AssetWidgetButton } from "./plugins/AssetWidget.plugin";
import { AbxUploadAdapterPlugin } from "./plugins/AbxUploadAdapter";

import { stylesheet } from "../stylesheet";
import { useParams, Navigate, useRouteLoaderData } from "react-router";
import { DecoupledEditor } from "@ckeditor/ckeditor5-editor-decoupled";
import { Box, Typography, LinearProgress } from "@mui/material";
import { OrganizationResponse, UserResponse } from "@akitabox/api-client";
import { makeUseServiceCall } from "../hooks/useServiceCall";
import { api } from "../api";
import {
  AssetSelectionDialog,
  buildAssetCardAsCKTable,
} from "../asset-selection-dialog/AssetSelectionDialog";
import { getCKEditorKey } from "../../utils/getCKEditorKey";
import { sanitizeXSS } from "../../utils/xss";
import { encodeText } from "../../utils/encodeText";
import { decodeText } from "../../utils/decodeText";
import { Color } from "../colors";
import {
  InsightsTableData,
  InsightsWidgetSelectionDialog,
} from "./insights/InsightsWidgetSelectionDialog";
import { getInsightsEnv } from "../../utils/getInsightsEnv";
import { DashboardModel } from "./insights/types";
import { base64AkitaboxFooterLogo } from "../../utils/getBase64FooterLogo";
import { useGlobalSnackbar } from "../consecutive-snackbar/GlobalSnackbar";
import InsightsService from "../services/InsightsService";

import "./ck-editor.css";
import { ExportPdfButton } from "./plugins/ExportPdf.plugin";
import { FilesWidgetButton } from "./plugins/FilesWidget.plugin";
import { AttachmentBuildingDialog } from "../attachment-building-dialog/AttachmentBuildingDialog";
import { useShellDOM } from "../hooks/useShellDOM";

const INSIGHTS_ENV = getInsightsEnv();
const CK_EDITOR_LICENSE_KEY = getCKEditorKey();

export const ReportBuilderEditor: FunctionComponent = () => {
  const { contentRef } = useShellDOM();
  const { simple } = useGlobalSnackbar();
  const { report: reportId } = useParams() as { report: string };
  const { organization, user, flags } = useRouteLoaderData("shell") as {
    organization: OrganizationResponse;
    user: UserResponse;
    flags: { [key: string]: boolean };
  };
  const organizationId = organization._id;

  const { report_builder } = user.permission_group;
  const reportBuilderPermissions = useMemo(
    () => ({ read: report_builder["read"], update: report_builder["update"] }),
    [report_builder]
  );

  const isReportBuilderEnabled = flags["report_builder"];

  const {
    data: reportContentResponse,
    error: reportContentError,
    isValidating: isLoadingReportContent,
  } = makeUseServiceCall(api.reportContent.getById)({
    organizationId,
    reportId,
  });

  const { data: reportResponse, error: reportError } = makeUseServiceCall(
    api.reports.getById
  )({
    organizationId,
    reportId,
  });

  const reportNameRef = useRef<ElementRef<"span">>(null);
  const [toolbarRef, setToolbarRef] = useState<ElementRef<"div"> | null>(null);
  const [documentOutlineRef, setDocumentOutlineRef] =
    useState<ElementRef<"div"> | null>(null);

  const [sisenseApp, setSisenseApp] = useState<any>(null);
  const [dashboards, setDashboards] = useState<DashboardModel[]>();
  const [selectedDashboard, setSelectedDashboard] = useState<string>("");
  const [showInsightsDialog, setShowInsightsDialog] = useState<boolean>(false);
  const [showAssetDialog, setShowAssetDialog] = useState<boolean>(false);
  const [showFilesDialog, setShowFilesDialog] = useState<boolean>(false);
  const [editor, setEditor] = useState<DecoupledEditor | null>(null);
  const [editorToolbar, setEditorToolbar] = useState<HTMLElement | null>(null);
  const [editorData, setEditorData] = useState<string>("");
  const [editorConfig] = useState<EditorConfig>({
    plugins: [
      AbxUploadAdapterPlugin,
      Alignment,
      AssetWidgetButton,
      FilesWidgetButton,
      Autoformat,
      Autosave,
      AutoImage,
      Bold,
      BlockQuote,
      CloudServices,
      DocumentList,
      DocumentOutline,
      Essentials,
      ExportPdf,
      ExportPdfButton,
      FontBackgroundColor,
      FontColor,
      FontFamily,
      FontSize,
      FormatPainter,
      Heading,
      Highlight,
      HorizontalLine,
      HtmlEmbed,
      Image,
      ImageCaption,
      ImageResize,
      ImageStyle,
      ImageToolbar,
      ImageUpload,
      ImageUtils,
      ImportWord,
      Indent,
      IndentBlock,
      InsightsWidgetButton,
      Italic,
      Link,
      Mention,
      PageBreak,
      Pagination,
      Paragraph,
      RemoveFormat,
      SlashCommand,
      SpecialCharacters,
      SpecialCharactersEssentials,
      Strikethrough,
      Subscript,
      Superscript,
      Table,
      TableCaption,
      TableCellProperties,
      TableColumnResize,
      TableOfContents,
      TableProperties,
      TableToolbar,
      Template,
      Underline,
    ],
    toolbar: [
      "abxExportPdf",
      "importWord",
      "|",
      "undo",
      "redo",
      "|",
      "pageNavigation",
      "|",
      "heading",
      "fontFamily",
      "fontSize",
      "bold",
      "italic",
      "underline",
      "fontColor",
      "fontBackgroundColor",
      "formatPainter",
      {
        icon: icons.pencil,
        label: "Highlight",
        items: [
          "highlight:yellowMarker",
          "highlight:greenMarker",
          "highlight:pinkMarker",
          "highlight:greenPen",
          "highlight:redPen",
          "removeHighlight",
        ],
      },
      {
        label: "More Formats",
        items: ["strikethrough", "superscript", "subscript"],
      },
      "|",
      {
        label: "AkitaBox",
        withText: true,
        icon: false,
        items: ["abxAssets", "abxInsights", "abxFiles"],
      },
      "|",
      "tableOfContents",
      "insertTable",
      "uploadImage",
      "pageBreak",
      {
        label: "Insert More",
        items: ["specialCharacters", "link", "blockQuote", "horizontalLine"],
      },
      "|",
      "alignment",
      "outdent",
      "indent",
      "bulletedList",
      "numberedList",
      "removeFormat",
    ],
    licenseKey: CK_EDITOR_LICENSE_KEY,
    fontFamily: {
      supportAllValues: true,
    },
    fontSize: {
      options: ["default", 8, 9, 10, 10.5, 11, 12, 14, 18, 24, 30, 36],
    },
    list: {
      properties: {
        styles: true,
      },
    },
    link: {
      addTargetToExternalLinks: true,
    },
    autosave: {
      save: async (editor) => {
        const editorData = editor.data.get();

        try {
          const sanitizedContent = sanitizeXSS(editorData);
          await api.reportContent.update({
            reportId,
            organizationId,
            // send base64 encoded content
            // (WAF blocks url encoded content that has a table)
            reportContent: { content: encodeText(sanitizedContent) },
          });
        } catch (err) {
          simple(
            "Failed saving report content. Update the document to try again.",
            { severity: "error" }
          );
        }
      },
    },
    image: {
      styles: {
        options: [
          {
            icon: "",
            name: "assetImageContained",
            title: "asset-image-contained",
            className: "asset-image-contained",
            modelElements: ["imageInline", "imageBlock"],
          },
          // I dunno why but we have to re-create default image styles if we default a new one, above
          {
            name: "alignLeft",
            icon: icons.objectLeft,
            title: "Left aligned image",
            className: "image-style-align-left",
            modelElements: ["imageBlock", "imageInline"],
          },
          {
            name: "alignCenter",
            icon: icons.objectCenter,
            title: "Centered image",
            className: "image-style-align-center",
            modelElements: ["imageBlock"],
          },
          {
            name: "alignRight",
            icon: icons.objectRight,
            title: "Right aligned image",
            className: "image-style-align-right",
            modelElements: ["imageBlock", "imageInline"],
          },
        ],
      },
      toolbar: [
        "toggleImageCaption",
        "|",
        "imageStyle:alignLeft",
        "imageStyle:alignCenter",
        "imageStyle:alignRight",
        "|",
        "resizeImage:original",
      ],
    },
    abxInsights: {
      setShowInsightsDialog,
    },
    abxAssets: {
      setShowAssetDialog,
    },
    abxFiles: {
      setShowFilesDialog,
    },
    abxUpload: {
      organizationId: organization._id,
    },
    table: {
      contentToolbar: [
        "toggleTableCaption",
        "tableRow",
        "tableColumn",
        "mergeTableCells",
        "tableProperties",
        "tableCellProperties",
      ],
    },
    // this needs to correlate with exportPdf config, both are set to A4 paper
    pagination: {
      pageWidth: "21cm",
      // A4 is supposed to be 29.7, but everything works better with 31.1
      pageHeight: "31.1cm",

      // once we include the main.css as part of the exported stylesheets to
      // the exportPdf plugin, it removes all margin
      pageMargins: {
        top: "0mm",
        bottom: "0mm",
        right: "0mm",
        left: "0mm",
      },
    },
    cloudServices: {
      tokenUrl: () => {
        return new Promise((resolve, reject) => {
          (async () => {
            try {
              const res = await api.ckeditor.getToken();
              const token: string = res.data;
              resolve(token);
            } catch (error) {
              reject(error);
            }
          })();
        });
      },
    },
    // this needs to correlate with pagination config, both are set to A4 paper
    exportPdf: {
      stylesheets: [
        "https://fonts.googleapis.com/css?family=Montserrat:400,500,700",
        "https://fonts.googleapis.com/css?family=Open+Sans:400,300,300italic,400italic,600,600italic,700,700italic,800,800italic",
        "https://fonts.googleapis.com/css2?family=Herr+Von+Muellerhoff&display=swap",
        "EDITOR_STYLES",
        "/static/main.css",
        "/static/ck-editor.css",
      ],
      fileName: () => {
        if (reportNameRef.current) {
          const { textContent: reportName } = reportNameRef.current;
          return reportName ? `${reportName}.pdf` : "";
        }
        return "";
      },
      converterOptions: {
        format: "A4",
        // once we include the main.css as part of the exported stylesheets to
        // the exportPdf plugin, it removes all margin, these don't matter
        margin_top: "16mm",
        margin_bottom: "16mm",
        margin_right: "12mm",
        margin_left: "12mm",
        page_orientation: "portrait",
        footer_html: `<div class="footer"><img src="${base64AkitaboxFooterLogo}"/></div>`,
        header_and_footer_css: `
        .footer {
            display: flex;
            flex-direction: row-reverse;
            margin-right: 20px;
          }
        
          .footer img {
            aspect-radio: 16/9;
            object-fit: cover;
            max-height: 32px;
          }`,
      },
    },
    importWord: {
      defaultStyles: true,
      commentsStyles: "full",
    },
    htmlEmbed: {
      showPreviews: true,
    },
  });

  useEffect(() => {
    if (editor && reportContentResponse) {
      const { content } = reportContentResponse.data;
      // loads and decodes existing content from current report
      if (content !== null) {
        setEditorData(decodeText(content));
      }
    }
  }, [editor, reportContentResponse]);

  const loadDashboards = useCallback(
    async (sisenseApp: any) => {
      try {
        const { data } = await sisenseApp.$$http.get(
          `${INSIGHTS_ENV}/api/v1/dashboards`
        );
        setDashboards(data);
      } catch (error) {
        simple("Failed loading sisense dashboards", { severity: "error" });
      }
    },
    [simple]
  );

  // Sisense JS integration
  const loadInsights = useCallback(() => {
    const script = document.createElement("script");
    script.id = "sisense-embed-script";
    // sisense.js breaks all angular routing upon being loaded into the DOM
    // sisense.v1.js fixes this issue, but it breaks pivot tables
    // we will want to monitor updates to this and future scripts to improve functionality
    script.src = `${INSIGHTS_ENV}/js/sisense.v1.js`;
    script.onload = async function () {
      try {
        // connect with persist=true to persist updates to dashboard
        const sisenseApp = await window.Sisense?.connect(INSIGHTS_ENV, true);
        InsightsService.sisenseApp = sisenseApp;
        setSisenseApp(sisenseApp);
        loadDashboards(sisenseApp);
      } catch (error) {
        simple("Failed connecting to sisense", { severity: "error" });
      }
    };
    document.body.appendChild(script);
  }, [loadDashboards, simple]);

  // setup insights once on page load
  useEffect(() => {
    const sisenseApp = InsightsService.sisenseApp;
    if (!sisenseApp) {
      loadInsights();
    } else {
      setSisenseApp(sisenseApp);
      loadDashboards(sisenseApp);
    }
  }, [loadInsights, loadDashboards]);

  const handleInsightsSelectionDialogClose = useCallback(() => {
    setShowInsightsDialog(false);
    setSelectedDashboard("");
  }, []);

  const handleWidgetMultiselect = useCallback(
    (imageUrls: string[], tableData: InsightsTableData[] | undefined) => {
      handleInsightsSelectionDialogClose();
      if (editor) {
        imageUrls.forEach((url: string) => {
          const viewFragment = editor.data.processor.toView(url);
          const modelFragment = editor.data.toModel(viewFragment);
          editor.model.insertContent(modelFragment);
        });

        if (tableData && tableData.length) {
          tableData.forEach((tableData: InsightsTableData) => {
            const table = buildHTMLTable(tableData);
            if (table) {
              const viewFragment = editor.data.processor.toView(
                renderToString(table)
              );

              const documentFragment = editor.data.toModel(viewFragment);
              editor.model.insertContent(
                documentFragment,
                editor.model.document.selection.getFirstPosition()
              );
            }
          });
        }
      }
    },
    [handleInsightsSelectionDialogClose, editor]
  );

  const buildHTMLTable = (tableData: InsightsTableData) => {
    const { columns, rows } = tableData;
    return (
      <table>
        <colgroup>
          {columns.map((col) => (
            <Fragment key={col.name}>
              <col />
            </Fragment>
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((col, idx) => (
              <th key={idx}>{col.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={rowIdx}>
              {row.map((rowData: any, cellIdx: any) => (
                <td key={cellIdx}>{rowData.data}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const calcTemplateRowHeight = useCallback(() => {
    if (!contentRef || !toolbarRef) return "auto";
    const height = contentRef.offsetHeight - toolbarRef.offsetHeight - 1;
    return `${height}px`;
  }, [contentRef, toolbarRef]);

  if (!isReportBuilderEnabled || !reportBuilderPermissions.read) {
    return <Navigate to="/" />;
  }

  if (reportError || reportContentError) {
    return <Navigate to="/report_builder" />;
  }

  return (
    <Box
      css={[
        CkEditorWrapperStyles.root,
        { gridTemplateRows: `auto ${calcTemplateRowHeight()}` },
      ]}
    >
      <div
        ref={(ref) => setToolbarRef(ref)}
        css={CkEditorWrapperStyles.toolbar}
      >
        <Box css={CkEditorWrapperStyles.reportNameWrapper}>
          <Typography variant="h6" css={CkEditorWrapperStyles.reportName}>
            <span title={reportResponse?.data.name} ref={reportNameRef}>
              {reportResponse?.data.name}
            </span>
          </Typography>
        </Box>
        {/* toolbar takes a bit of time to generate, show it after it does  */}
        {reportBuilderPermissions.update && editorToolbar && (
          <div ref={(ref) => ref?.appendChild(editorToolbar)}></div>
        )}
        {isLoadingReportContent && <LinearProgress />}
      </div>
      {sisenseApp && dashboards && (
        <InsightsWidgetSelectionDialog
          open={showInsightsDialog}
          appContext={sisenseApp}
          sisenseEnv={INSIGHTS_ENV}
          dashboards={dashboards}
          onClose={handleInsightsSelectionDialogClose}
          selectedDashboard={selectedDashboard}
          setSelectedDashboard={setSelectedDashboard}
          onWidgetMultiselect={handleWidgetMultiselect}
        ></InsightsWidgetSelectionDialog>
      )}
      {showAssetDialog && (
        <AssetSelectionDialog
          open={showAssetDialog}
          onClose={async (result) => {
            if (result.action === "cancel") {
              setShowAssetDialog(false);
              return;
            }
            let doc;
            if (editor) {
              doc = editor.ui.getEditableElement()?.ownerDocument;
            }

            if (!doc) {
              throw new Error("Missing document");
            }

            for (const asset of result.assets.reverse()) {
              const assetCardElement = await buildAssetCardAsCKTable(
                asset,
                result.attachments[asset._id].attachments,
                result.template,
                result.attachments[asset._id].default
              );
              if (editor) {
                const viewFragment = editor.data.processor.toView(
                  renderToString(assetCardElement)
                );

                const documentFragment = editor.data.toModel(viewFragment);
                editor.model.insertContent(
                  documentFragment,
                  editor.model.document.selection.getFirstPosition()
                );
              }
            }
            setShowAssetDialog(false);
          }}
          organizationId={organizationId}
        />
      )}
      {showFilesDialog && (
        <AttachmentBuildingDialog
          organization={organization._id}
          open={true}
          onClose={(documents) => {
            if (documents.length && editor) {
              for (const { document } of documents) {
                const imageUrl = `<img src="${document.public_thumbnail_url_display}"/>`;
                const viewFragment = editor?.data.processor.toView(imageUrl);
                const modelFragment = editor?.data.toModel(viewFragment);
                editor?.model.insertContent(modelFragment);
              }
            }
            setShowFilesDialog(false);
          }}
        />
      )}
      <div
        ref={(ref) => setDocumentOutlineRef(ref)}
        css={CkEditorWrapperStyles.documentOutline}
      ></div>
      {documentOutlineRef && (
        <div css={CkEditorWrapperStyles.container}>
          <CKEditor<DecoupledEditor>
            disabled={
              !reportBuilderPermissions.update || isLoadingReportContent
            }
            editor={DecoupledEditor}
            data={editorData}
            config={{
              ...editorConfig,
              documentOutline: { container: documentOutlineRef },
            }}
            onChange={(_event, editor) => {
              setEditorData(editor.getData());
            }}
            onReady={(editor: DecoupledEditor) => {
              // make the editing window flex to fill the remaining space when editing
              // this works because our root element is a flex box
              setEditor(editor);
              // set the toolbar once the editor is ready so we can render it on the DOM
              setEditorToolbar(editor.ui.view.toolbar.element);
              // set our config for our custon config button
              editor.config.set("abxExportPdf", { organizationId, reportId });
            }}
          />
        </div>
      )}
    </Box>
  );
};

const CkEditorWrapperStyles = stylesheet({
  root: () => ({
    display: "grid",
    gridTemplateColumns: "minmax(auto, 0.15fr) 1fr",
    backgroundColor: "var(--ck-color-base-foreground)",
    gridTemplateAreas: '"Toolbar Toolbar" "Sidebar Content"',
  }),
  toolbar: () => ({ gridArea: "Toolbar" }),
  documentOutline: (theme) => ({
    gridArea: "Sidebar",
    overflowY: "auto",
    padding: theme.spacing(6, 0, 0, 2),
  }),
  container: (theme) => ({
    gridArea: "Content",
    overflowY: "auto",
    padding: theme.spacing(4),
    ".ck-editor__editable_inline": {
      /* Set the dimensions of the "page". */
      width: "216mm",
      minHeight: "297mm",
      /* Keep the "page" off the boundaries of the container. */
      padding: "20mm 12mm",
      border: "1px hsl( 0,0%,82.7% ) solid",
      borderRadius: "var(--ck-border-radius)",
      backgroundColor: "white",
      boxSizing: "border-box",
      /* The "page" should cast a slight shadow (3D illusion). */
      boxShadow: "0 0 5px hsla( 0,0%,0%,.1 )",
      /* Center the "page". */
      margin: "0 auto",
      /* Force all images in ck-editor to conform to the width of the page, this helps mostly with exporting to pdf */
      ".image.ck-widget img": {
        maxWidth: "191mm",
        height: "auto",
      },
      ".raw-html-embed__content-wrapper": {
        resize: "both",
      },
      /* Workaround to contain asset images within the same aspect ratio */
      ".asset-image-contained img": {
        aspectRatio: "4 / 3",
        objectFit: "contain",
      },
    },
  }),
  reportNameWrapper: (theme) => ({
    padding: theme.spacing(1, 2),
    backgroundColor: Color.White,
  }),
  reportName: () => ({
    maxWidth: "528px",
    fontWeight: "bold",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  }),
});
